import React from "react";
import { Field, Form, Formik, FormikConfig } from "formik";
import { object, string } from "yup";

import { PlainModal, StandardModalHeader } from "../../../general/Modal/Modal";
import { useUpdateUser } from "../../../../hooks/useUpdateUser";
import { useToaster } from "../../../general/ToastMessages/useToaster";
import { FormRow } from "../../../forms/FormRow";
import { useUser } from "../../../../hooks/useUser";
import { submitHelper } from "../../../../helpers/form";
import { userRest } from "../../../../lib/restClient";
import { Button } from "../../../general/Button/Button";
import { ButtonWithLoader } from "../../../general/ButtonWithLoader/ButtonWithLoader";

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateNameModal: React.FC<Props> = ({ show, onClose }) => {
    const user = useUser();
    const updateUser = useUpdateUser();
    const { error } = useToaster();

    const form: FormikConfig<{ preferredName: string }> = {
        initialValues: {
            preferredName: user.preferredName ?? "",
        },
        validationSchema: object().shape({
            preferredName: string().required("Preferred name is required"),
        }),
        onSubmit: submitHelper({
            handler: async ({ preferredName }) => {
                const previousName = user.preferredName ?? "";

                try {
                    updateUser({
                        ...user,
                        preferredName,
                        name: preferredName,
                    });

                    await userRest.update(user.id, { preferredName });
                } catch {
                    error("Failed to update profile");

                    updateUser({
                        ...user,
                        preferredName: previousName,
                        name: previousName,
                    });
                }
            },
        }),
    };

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>
                <h4 className="m-0">Update Name</h4>
            </StandardModalHeader>
            <Formik {...form}>
                {({ isValid, isSubmitting }) => (
                    <Form>
                        <FormRow
                            showErrorMessage={false}
                            fieldName="preferredName"
                            label="Preferred Name"
                        >
                            <Field
                                name="preferredName"
                                className="form-control"
                            />
                        </FormRow>
                        <footer className="d-flex justify-content-end gap-1">
                            <Button
                                className="mr-2"
                                variant="secondary"
                                size="lg"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>

                            <ButtonWithLoader
                                disabled={!isValid}
                                loading={!!isSubmitting}
                                type="submit"
                                size="lg"
                            >
                                Save
                            </ButtonWithLoader>
                        </footer>
                    </Form>
                )}
            </Formik>
        </PlainModal>
    );
};
